/**
 * WebSocket client for Playbook Live (full-duplex playbook training).
 *
 * Runs alongside `kbLiveWebSocketService`, which serves knowledge-base
 * tracks only. Every playbook track runs here.
 *
 * Wire protocol, deliberately simple:
 *   text frames   -> JSON control events, both directions
 *   binary frames -> raw PCM16 @ 24 kHz, both directions
 *
 * Stage and signal strings mirror backend/src/constants/trainingConstants.js.
 * The service asserts the same mirror in Python; keep all three in step.
 */

import { getAuth } from 'firebase/auth';

import { config } from '../config';
import type { LivePlaybookFrameData } from './livePlaybookFrames';

export type PlaybookStage =
  | 'S1_OPENING'
  | 'S2_DISCOVERY'
  | 'S3_POSITIONING'
  | 'S4_CLOSING'
  | 'S5_WRAP';

export type CloseSignalType = 'timeline' | 'budget' | 'authority' | 'competition' | 'stall';
export type CloseSignalOutcome = 'converted' | 'partial' | 'missed' | 'pending';

export const STAGE_LABELS: Record<PlaybookStage, string> = {
  S1_OPENING: 'Opening',
  S2_DISCOVERY: 'Discovery',
  S3_POSITIONING: 'Positioning',
  S4_CLOSING: 'Closing',
  S5_WRAP: 'Wrap-up',
};

/**
 * The persona fields session_started still carries. They are the voice
 * model's second-person instructions ("You are..."), so the page never shows
 * them: the rep reads the track's RepBriefing (hooks/useTrackRepBriefing).
 */
export interface MeetingPremise {
  role: string;
  accountProfile: string;
  buyingObjective: string;
  manner: string;
}

export interface SessionStartedData {
  sessionId: string;
  trackName?: string;
  agendaTotal: number;
  targetDurationMinutes: number;
  mode: 'playbook_live';
  premise?: MeetingPremise;
}

export interface StageChangedData {
  old_phase: PlaybookStage | null;
  new_phase: PlaybookStage;
}

export interface TranscriptDeltaData {
  speaker: 'salesperson' | 'ai_customer';
  text: string;
}

export interface AgendaProgressData {
  total: number;
  satisfied: number;
  /** Concerns still open (raised, not yet resolved). */
  raised: number;
  /** Every concern the customer has raised so far, open or resolved. */
  raisedSoFar?: number;
  remaining: number;
}

export interface CloseSignalResolvedData {
  signalId: string;
  type: CloseSignalType;
  outcome: CloseSignalOutcome;
}

export interface SessionCompletedData {
  itemsCompleted: number;
  itemCount: number;
  finalStage: PlaybookStage;
  closeSignals: Array<{ type: CloseSignalType; outcome: CloseSignalOutcome }>;
  /** The voice connection dropped and the session ended early (saved as abandoned). */
  endedEarly?: boolean;
  endReason?: string | null;
}

interface Handlers {
  onSessionStarted?: (data: SessionStartedData) => void;
  onStageChanged?: (data: StageChangedData) => void;
  onTranscriptDelta?: (data: TranscriptDeltaData) => void;
  onDiscoveryTurn?: (data: { aiText: string }) => void;
  onQuestionDetected?: (data: any) => void;
  onStoredAnswerGenerated?: (data: any) => void;
  onAnswerGenerated?: (data: any) => void;
  onAnswerError?: (data: any) => void;
  onLandmineAlert?: (data: any) => void;
  onCompetitorAlert?: (data: any) => void;
  onAgendaProgress?: (data: AgendaProgressData) => void;
  onCloseSignal?: (data: { type: CloseSignalType }) => void;
  onCloseSignalResolved?: (data: CloseSignalResolvedData) => void;
  onSessionCompleted?: (data: SessionCompletedData) => void;
  /** The live call's checklist, run on this session (services playbook_live/checklist.py):
   * a check or note it made, or the snapshot that confirms the rep's own edit. */
  onPlaybookFrame?: (type: 'playbook_item' | 'playbook_snapshot', data: LivePlaybookFrameData) => void;
  onCustomerAudio?: (pcm: ArrayBuffer) => void;
  onError?: (message: string) => void;
  onConnectionChange?: (connected: boolean) => void;
}

class PlaybookLiveWebSocketService {
  private socket: WebSocket | null = null;
  private handlers: Handlers = {};
  private isManualDisconnect = false;

  setHandlers(handlers: Handlers): void {
    this.handlers = handlers;
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  async connect(trackId: string, sessionId?: string): Promise<void> {
    this.isManualDisconnect = false;

    const token = await getAuth().currentUser?.getIdToken();
    if (!token) throw new Error('You need to be signed in to start a training session.');

    return new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(config.server.playbookLiveUrl);
      // Customer audio arrives as binary; without this every frame is a Blob
      // and playback has to round-trip through FileReader.
      socket.binaryType = 'arraybuffer';
      this.socket = socket;

      socket.onopen = () => {
        this.handlers.onConnectionChange?.(true);
        socket.send(JSON.stringify({ type: 'start_session', data: { trackId, token, sessionId } }));
        resolve();
      };

      socket.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) {
          this.handlers.onCustomerAudio?.(event.data);
          return;
        }
        try {
          const message = JSON.parse(event.data);
          this.dispatch(message?.type, message?.data ?? {});
        } catch {
          // A single malformed frame must not tear down a live call.
          console.error('PlaybookLive: could not parse server message');
        }
      };

      socket.onerror = () => {
        this.handlers.onConnectionChange?.(false);
        reject(new Error('Could not connect to the training service.'));
      };

      socket.onclose = () => {
        this.handlers.onConnectionChange?.(false);
        // No auto-reconnect: gpt-live-1 cannot resume a dropped session, so a
        // silent reconnect would hand the rep a customer with no memory of the
        // conversation. Surfacing the drop is the honest behaviour.
        if (!this.isManualDisconnect) {
          this.handlers.onError?.('The training session disconnected.');
        }
      };
    });
  }

  /**
   * The rep's tick, untick or note in the playbook panel, in the live call's
   * sales_playbook_update shape: the session's checklist applies it, locks the
   * item from the AI, and answers with a playbook_snapshot. Fire-and-forget.
   */
  sendPlaybookAction(action: { updateType: string; [key: string]: unknown }): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'playbook_action', data: action }));
    }
  }

  /** Forward one microphone frame. Fire-and-forget by design. */
  sendAudio(pcm: ArrayBuffer): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(pcm);
  }

  endSession(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'end_session' }));
    }
  }

  disconnect(): void {
    this.isManualDisconnect = true;
    this.endSession();
    this.socket?.close();
    this.socket = null;
  }

  private dispatch(type: string | undefined, data: any): void {
    switch (type) {
      case 'session_started':
        this.handlers.onSessionStarted?.(data);
        break;
      case 'phase_changed':
        this.handlers.onStageChanged?.(data);
        break;
      case 'transcript_delta':
        this.handlers.onTranscriptDelta?.(data);
        break;
      case 'discovery_turn':
        this.handlers.onDiscoveryTurn?.(data);
        break;
      case 'question_detected':
        this.handlers.onQuestionDetected?.(data);
        break;
      case 'stored_answer_generated':
        this.handlers.onStoredAnswerGenerated?.(data);
        break;
      case 'answer_generated':
        this.handlers.onAnswerGenerated?.(data);
        break;
      case 'answer_error':
        this.handlers.onAnswerError?.(data);
        break;
      case 'landmine_alert':
        this.handlers.onLandmineAlert?.(data);
        break;
      case 'competitor_alert':
        this.handlers.onCompetitorAlert?.(data);
        break;
      case 'agenda_progress':
        this.handlers.onAgendaProgress?.(data);
        break;
      case 'close_signal':
        this.handlers.onCloseSignal?.(data);
        break;
      case 'close_signal_resolved':
        this.handlers.onCloseSignalResolved?.(data);
        break;
      case 'playbook_item':
      case 'playbook_snapshot':
        this.handlers.onPlaybookFrame?.(type, data);
        break;
      case 'session_completed':
        this.handlers.onSessionCompleted?.(data);
        break;
      case 'error':
        this.handlers.onError?.(data?.message ?? 'Something went wrong.');
        break;
      default:
        break;
    }
  }
}

export const playbookLiveWebSocketService = new PlaybookLiveWebSocketService();
